/* eslint-disable max-lines */
import { Component } from '@angular/core';
import { AllInclusiveService } from '@app/core/services/all-inclusive.service';
import { NotificationClientTodo, NotificationResult } from '@app/core/services/notification.service.types';
import { getIncludeFinishedLabel } from '@app/shared/misc/getIncludeFinishedLabel';
import { showClientDialog } from '@app/shared/misc/showClientDialog';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Client } from '../core/entity/client';
import { isNetherlands, isSweden, Language } from '../core/entity/locale';
import { getTranslationProvider } from '../core/i18n/getTranslationProvider';
import { TranslatableComponent } from '../core/i18n/TranslatableComponent';
import { TranslatableStringType } from '../core/i18n/types';
import { ClientService } from '../core/services/clients.service';
import { NotificationService } from '../core/services/notification.service';
import { UserService } from '../core/services/user.service';
import { FirmSelectors } from '../core/state/firm/firm.selectors';
import { ToastActions } from '../core/state/toast/toast.actions';
import { ClientType, UserType } from '../core/state/types';
import { UserSelectors } from '../core/state/users/users.selectors';
import { UserSettingsStorage } from '../core/storage/user.settings.storage';
import { TableColumn } from '../shared/components/table/interfaces/table-column';
import { translateColumnHeaders } from '../shared/components/table/misc/translate-column-header';
import { TodayDate } from '../shared/misc/today.date';
// import { AnalyticEventTracker } from '../shared/decorators/analytics.decorator';
// import { AnalyticsCategory } from '../core/entity/analytics/analytics-categories';
// import { AnalyticsAction } from '../core/entity/analytics/analytics-actions';

enum WarningDialogResult {
  NOT_ANSWERED,
  YES,
  NO,
}

type NotificationsTranslations = {
  onlyShowRowsWithContent: TranslatableStringType;
  onlyShowRowsWithContentInclTaxLabel: TranslatableStringType;
};

@Component({
  templateUrl: './notifications.component.html',
  providers: [getTranslationProvider('NotificationsComponent')],
})
export class NotificationsComponent extends TranslatableComponent<NotificationsTranslations> {
  public allTodos: NotificationClientTodo[] = [];
  public todos: NotificationClientTodo[] = [];
  public loading = false;
  public selectedResponsibleUserId = 0;
  public selectedClient: Client = new Client('');
  public startDate: string;
  public endDate: string;
  public showAllUserWarningDialog = false;
  public showSkvGetStartedDialog = false;
  public showPermissionInfoDialog = false;
  public defaultNumberOfRows = 50;
  public columns$: Observable<TableColumn[]>;
  private columns: TableColumn[] = [
    {
      field: 'client.customerNumber',
      header: 'customerNumber',
      width: '150px',
      class: 'text-center white-space-normal',
    },
    {
      field: 'client.corporateIdentity',
      header: 'orgNumber',
      width: '150px',
      class: 'text-center white-space-normal',
    },
    { field: 'client.name', header: 'name', class: 'text-center white-space-normal' },
    {
      field: 'client.responsible.initials',
      header: 'responsibleInitials',
      width: '4%',
      class: 'text-center white-space-normal',
    },
    { field: 'todo.UnhandledDocuments', header: 'unhandledDocuments', class: 'text-center white-space-normal' },
    { field: 'todo.UnpaidSupplierInvoices', header: 'unpaidSupplierInvoices', class: 'text-center white-space-normal' },
    { field: 'todo.PaymentsToRemediate', header: 'paymentsToRemediate', class: 'text-center white-space-normal' },
    {
      field: 'todo.AutoRegisteredInvoice',
      header: 'autoRegisteredInvoice',
      class: 'text-center white-space-normal',
      hiddenForCountries: ['NL'],
    },
    {
      field: 'todo.AutoRegisteredReceipt',
      header: 'autoRegisteredReceipt',
      class: 'text-center white-space-normal',
      hiddenForCountries: ['NL'],
    },
    {
      field: 'todo.UncoupledQredTransactions',
      header: 'uncoupledQredTransactions',
      class: 'text-center white-space-normal',
      hiddenForCountries: ['NL'],
    },
    {
      field: 'todo.EinvoiceReceived',
      header: 'incommingEinvoice',
      headerTooltip: 'tooltipIncommingEinvoice',
      visible: false,
      class: 'text-center white-space-normal',
      hiddenForCountries: ['NL'],
    },
    {
      field: 'todo.EinvoiceSent',
      header: 'outgoingEinvoice',
      headerTooltip: 'tooltipOutgoingEinvoice',
      visible: false,
      class: 'text-center white-space-normal',
      hiddenForCountries: ['NL'],
    },
    {
      field: 'todo.TaxAccount',
      header: 'taxAccountBalance',
      class: 'text-center white-space-normal',
      hiddenForCountries: ['NL'],
    },
    {
      field: 'program',
      header: 'openWith',
      width: '90px',
      sortable: false,
      class: 'text-center white-space-normal',
      exportable: false,
    },
  ];

  public onlyShowRowsWithContentLabel = '';
  public onlyShowRowsWithContent = false;
  public onlyShowRowsWithContentLabel$: Observable<string>;

  private onlyShowRowsWithContentLabelBS: BehaviorSubject<NotificationClientTodo[]>;
  private clients: Client[] = [];
  private uss = new UserSettingsStorage();
  private allUserWarningSubject = new BehaviorSubject(WarningDialogResult.NOT_ANSWERED);
  private lastSelectedResponsibleUserId: number;

  get lastUpdatedDate(): Date | null {
    return this.notificationService.getLastUpdatedDate();
  }

  get dateFormat() {
    const lang = this.getActiveLanguage();
    return lang === Language.NL ? 'dd-MM-yyyy HH:mm' : 'yyyy-MM-dd HH:mm';
  }

  get isLoggedInWithAllInclusiveService() {
    return this.allInclusiveService.isLoggedInWithAnyAllInclusiveService;
  }

  get isFilterDatesValid() {
    return this.startDate && this.endDate;
  }

  get defaultStartDate() {
    return this.todayDate.subtractDays(7);
  }

  private todayDate;

  // eslint-disable-next-line max-params
  constructor(
    private userService: UserService,
    private clientService: ClientService,
    private notificationService: NotificationService,
    private allInclusiveService: AllInclusiveService,
    private dialogService: DialogService,
  ) {
    super();
    this.todayDate = new TodayDate();
    this.loadUserSettings();

    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns));
    this.onlyShowRowsWithContentLabelBS = new BehaviorSubject(null);
    this.onlyShowRowsWithContentLabel$ = this.getObservableForTranslatedShowRowsCheckboxLabel();
  }

  initEverything = () => forkJoin([this.loadUsers(), this.loadClients()]).subscribe(() => this.loadTodoInformation());

  // @AnalyticEventTracker({ category: AnalyticsCategory.Notifications, action: AnalyticsAction.LoadNotifications })
  loadTodoInformation(force = false) {
    if (!this.isFilterDatesValid) {
      return;
    }

    this.getLoadTodoInformationObservable(force)
      .pipe(
        first(),
        catchError(() => of([])), // catchError is partially handled by getLoadTodoInformationObservable so let's return an empty array)
      )
      .subscribe((result) => {
        this.saveUserSettings();
        this.lastSelectedResponsibleUserId = this.selectedResponsibleUserId;
        this.allTodos = result;
        this.todos = this.filterTodos(result);
        this.loading = false;
      });
  }

  callAgent(client: ClientType, task = '', url = '') {
    const agentCallerElement = document.getElementById('agentCaller') as HTMLAnchorElement;

    if (!this.allInclusiveService.isLoggedInWithAnyAllInclusiveService) {
      this.allInclusiveService.showMissingAllInclusiveServiceWarning();
      return;
    }

    this.store
      .select(FirmSelectors.selectCountryCode)
      .pipe(
        first(),
        switchMap((countryCode) => {
          if (!isSweden(countryCode)) {
            return of({ url: this.buildBlappUrl(client, url), target: '_blank' });
          }
          return this.clientService
            .getAgentUrl(client.id)
            .pipe(map(({ agentCall }) => ({ url: this.buildAgentUrl(agentCall, task), target: null })));
        }),
      )
      .subscribe(({ url, target }) => {
        if (target) {
          agentCallerElement.target = target;
        }
        agentCallerElement.href = url;
        agentCallerElement.click();
      });
  }

  onSkvDialogTagClick(event: Event) {
    const isHtmlElement = event.target instanceof HTMLElement;
    const hasDataClickKey = isHtmlElement && event.target.hasAttribute('data-click-key');
    const dataClickKeyValue = hasDataClickKey && event.target.dataset['clickKey'];

    if (dataClickKeyValue !== 'showExistingClient') {
      return;
    }

    this.showSkvGetStartedDialog = false;
    this.showPermissionInfoDialog = false;
    this.showExistingClient(this.selectedClient, 'cloudServices');
  }

  showExistingClient(client: Client, tabToOpen: string = null) {
    this.selectedClient = client;

    showClientDialog({
      clientService: this.clientService,
      dialogService: this.dialogService,
      translationService: this.translationService,
      id: client.id,
      store: this.store,
      tabToOpen,
    })
      .pipe(
        first(),
        map((result) => result?.client?.id),
        filter((clientId) => !isNaN(clientId) && clientId !== -1),
        switchMap(() => this.loadClients()),
      )
      .subscribe(() => {
        this.loadTodoInformation();
      });
  }

  allUserWarningConfirm() {
    this.allUserWarningSubject.next(WarningDialogResult.YES);
  }

  allUserWarningCancel() {
    this.allUserWarningSubject.next(WarningDialogResult.NO);
  }

  paginatorTriggered(event: { rows: number }) {
    this.uss.saveSetting(UserSettingsStorage.NOTIFICATION_NUMBER_OF_ROWS, `${event.rows}`);
  }

  openTaxAccount() {
    if (!this.allInclusiveService.isLoggedInWithAnyAllInclusiveService) {
      this.allInclusiveService.showMissingAllInclusiveServiceWarning();
      return;
    }

    window.open(
      'https://www.skatteverket.se/foretag/etjansterochblanketter/allaetjanster/tjanster/skattekonto',
      '_blank',
    );
  }

  callBlapp() {
    if (!this.allInclusiveService.isLoggedInWithAnyAllInclusiveService) {
      this.allInclusiveService.showMissingAllInclusiveServiceWarning();
      return;
    }

    window.open(this.buildBlappUrl(), '_blank');
  }

  onOnlyShowRowsWithContentChange(onlyShowRowsWithContent: boolean) {
    this.onlyShowRowsWithContent = onlyShowRowsWithContent;
    this.uss.saveSetting(UserSettingsStorage.NOTIFICATION_ONLY_SHOW_ROWS_WITH_CONTENT, String(onlyShowRowsWithContent));
    this.todos = this.filterTodos(this.allTodos);
  }

  openBlappInNewTab(client: ClientType) {
    window.open(this.buildBlappUrl(client), '_blank');
  }

  // eslint-disable-next-line complexity, @typescript-eslint/no-unused-vars
  private buildBlappUrl(client?: ClientType, _subUrl?: string) {
    return `${environment.blappWeb}/${client?.cloudApiKey || ''}?lang=${this.getActiveLanguage()}`;

    // TO BE USED WHEN WE HAVE SUBURL AND PUBLIC KEY IN PLACE IN BLAPP

    // if (subUrl && client?.cloudApiKey) {
    //   return `${environment.blappWeb}${subUrl}?lang=${this.getActiveLanguage()}&publicKey=${client.cloudApiKey}`;
    // }
    // if (subUrl) {
    //   return `${environment.blappWeb}${subUrl}?lang=${this.getActiveLanguage()}`;
    // }
    // if (client?.cloudApiKey) {
    //   return `${environment.blappWeb}/${client.cloudApiKey}?lang=${this.getActiveLanguage()}`;
    // }
    // return `${environment.blappWeb}?lang=${this.getActiveLanguage()}`;
  }

  private buildAgentUrl(agentCall: string, task: string) {
    let href = agentCall;
    if (task) {
      href += `&task=${task}`;
    }

    const jwt = sessionStorage.getItem('jwt') || '';
    href += `&jwt=${jwt}`;
    return href;
  }

  private loadUserSettings() {
    this.selectedResponsibleUserId = this.uss.loadSettingAsNumber(
      UserSettingsStorage.NOTIFICATION_RESPONSIBLE_USER,
      -1, // default when not set in storage
    );

    this.allUserWarningSubject.next(
      this.uss.loadSettingAsNumber(
        UserSettingsStorage.NOTIFICATION_WARNING_DIALOG_RESULT,
        WarningDialogResult.NOT_ANSWERED,
      ),
    );
    this.defaultNumberOfRows = this.uss.loadSettingAsNumber(
      UserSettingsStorage.NOTIFICATION_NUMBER_OF_ROWS,
      this.defaultNumberOfRows,
    );

    const oneWeekBackDate = this.todayDate.subtractDays(7);
    const defaultStartDate = this.todayDate.getIsoFormattedDate(oneWeekBackDate);
    const defaultEndDate = this.todayDate.getTodayAsString();

    this.startDate = this.uss.loadSetting(UserSettingsStorage.NOTIFICATION_START_DATE, defaultStartDate);
    this.endDate = this.uss.loadSetting(UserSettingsStorage.NOTIFICATION_END_DATE, defaultEndDate);
    this.onlyShowRowsWithContent = this.uss.loadSettingAsBoolean(
      UserSettingsStorage.NOTIFICATION_ONLY_SHOW_ROWS_WITH_CONTENT,
      false,
    );
  }

  private saveUserSettings() {
    this.uss.saveSetting(UserSettingsStorage.NOTIFICATION_RESPONSIBLE_USER, `${this.selectedResponsibleUserId}`);

    // if the user has answered YES on the warning, then we store that so the user don't have to answer again.
    if (this.allUserWarningSubject.getValue() === WarningDialogResult.YES) {
      this.uss.saveSetting(UserSettingsStorage.NOTIFICATION_WARNING_DIALOG_RESULT, `${WarningDialogResult.YES}`);
    }

    this.uss.saveSetting(UserSettingsStorage.NOTIFICATION_START_DATE, this.startDate);
    this.uss.saveSetting(UserSettingsStorage.NOTIFICATION_END_DATE, this.endDate);
  }

  private loadUsers() {
    const allUsers$ = this.store.select(UserSelectors.activeUsersWithAll).pipe(
      filter((users) => users.length > 0),
      take(1),
    );

    return forkJoin([this.userService.getCurrentUser(), allUsers$]).pipe(
      map(([currentUser, users]) => {
        const existsUserInList = (list: UserType[], userId: number) =>
          list.some((user: UserType) => user.id === userId);
        this.selectedResponsibleUserId =
          this.selectedResponsibleUserId >= 0 ? this.selectedResponsibleUserId : currentUser.id;

        if (!existsUserInList(users, this.selectedResponsibleUserId)) {
          this.selectedResponsibleUserId = currentUser.id;
        }

        this.lastSelectedResponsibleUserId = this.selectedResponsibleUserId;
        return true;
      }),
    );
  }

  private loadClients() {
    const hasCloudApiKey = (client: Client) => !!client.cloudApiKey;
    const isArchived = (client: Client) => client.archived;

    return this.clientService.getAllClients().pipe(
      map((clients: Client[]) => {
        this.clients = clients.filter((client) => hasCloudApiKey(client) && !isArchived(client));
        return true;
      }),
    );
  }

  // eslint-disable-next-line max-lines-per-function
  private getLoadTodoInformationObservable(force: boolean): Observable<NotificationClientTodo[]> {
    const loadTodosForResponsibleUser$ = of(true).pipe(
      tap(() => {
        this.loading = true;
        this.todos = [];
      }),
      switchMap(() =>
        this.notificationService.loadTodoInformationForResponsibleUser(
          this.selectedResponsibleUserId,
          this.startDate,
          this.endDate,
          force,
        ),
      ),
      map((result) => this.mapToClientTodoComposite(result)),
      map((result) => this.sortClientTodoComposite(result)),
      tap((result) => {
        if (result.some((composite) => !composite.todo.success)) {
          this.store.dispatch(
            ToastActions.showErrorMessage({
              summary: this.translate('couldNotLoadNotificationForOneOrMore'),
              detail: this.translate('verifyAndChangeCloudDatabaseKeyMessage'),
            }),
          );
        }
      }),
    );

    if (!this.isAllUsersSelected(this.selectedResponsibleUserId)) {
      return loadTodosForResponsibleUser$;
    }

    if (this.allUserWarningSubject.getValue() === WarningDialogResult.NOT_ANSWERED) {
      this.showAllUserWarningDialog = true;
    }

    return this.allUserWarningSubject.asObservable().pipe(
      // waiting for an answer in the dialog
      filter((dialogResult: WarningDialogResult) => dialogResult !== WarningDialogResult.NOT_ANSWERED),
      take(1),
      switchMap((dialogResult: WarningDialogResult) => {
        this.showAllUserWarningDialog = false;

        if (dialogResult === WarningDialogResult.NO) {
          // reset the dialog result to not answered and change back to last selected user
          this.allUserWarningSubject.next(WarningDialogResult.NOT_ANSWERED);
          this.selectedResponsibleUserId = this.lastSelectedResponsibleUserId;
          return EMPTY;
        }

        return loadTodosForResponsibleUser$;
      }),
    );
  }

  private mapToClientTodoComposite(todoResult: NotificationResult[]): NotificationClientTodo[] {
    const defaultTodo = {
      AutoRegisteredInvoice: 0,
      AutoRegisteredReceipt: 0,
      UncoupledQredTransactions: 0,
      UnhandledDocuments: 0,
      UnpaidSupplierInvoices: 0,
      UnpaidDomesticCurrencySupplierInvoice: 0,
      UnpaidForeignCurrencySupplierInvoice: 0,
      PaymentsToRemediate: 0,
      EinvoiceReceived: 0,
      EinvoiceSent: 0,
      TaxAccount: 'INACTIVE',
    };

    return todoResult.reduce((acc, r) => {
      const clients = this.clients.filter((client) => client.cloudApiKey === r.cloudApiKey);
      if (!clients.length) {
        return acc;
      }

      const todo = { ...defaultTodo, ...r.data, success: r.success };
      todo.UnpaidSupplierInvoices =
        todo.UnpaidDomesticCurrencySupplierInvoice + todo.UnpaidForeignCurrencySupplierInvoice;

      return [...acc, ...clients.map((client) => ({ client, todo }))];
    }, []);
  }

  private sortClientTodoComposite(list: NotificationClientTodo[]) {
    // eslint-disable-next-line complexity
    list.sort((a, b) => {
      let sortResult = b.todo.UnhandledDocuments - a.todo.UnhandledDocuments;

      if (sortResult === 0) {
        sortResult = b.todo.UnpaidSupplierInvoices - a.todo.UnpaidSupplierInvoices;
      }

      if (sortResult === 0) {
        sortResult = b.todo.PaymentsToRemediate - a.todo.PaymentsToRemediate;
      }

      if (sortResult === 0) {
        sortResult = b.todo.AutoRegisteredInvoice - a.todo.AutoRegisteredInvoice;
      }

      if (sortResult === 0) {
        sortResult = b.todo.AutoRegisteredReceipt - a.todo.AutoRegisteredReceipt;
      }

      if (sortResult === 0) {
        const aValue = a.todo.TaxAccount;
        const bValue = b.todo.TaxAccount;
        // eslint-disable-next-line no-nested-ternary
        sortResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      if (sortResult === 0) {
        sortResult = b.todo.EinvoiceReceived - a.todo.EinvoiceReceived;
      }

      if (sortResult === 0) {
        sortResult = b.todo.EinvoiceSent - a.todo.EinvoiceSent;
      }

      return sortResult;
    });

    return list;
  }

  private isAllUsersSelected(userId: string | number) {
    return +userId === 0;
  }

  private getObservableForTranslatedShowRowsCheckboxLabel() {
    return combineLatest([this.translationsLoaded$, this.onlyShowRowsWithContentLabelBS]).pipe(
      withLatestFrom(this.store.select(FirmSelectors.selectCountryCode)),
      map(([[translations, todos], countryCode]) => {
        return isNetherlands(countryCode)
          ? { todos, label: translations.onlyShowRowsWithContent }
          : { todos, label: translations.onlyShowRowsWithContentInclTaxLabel };
      }),
      map(({ todos, label }) => {
        return getIncludeFinishedLabel({
          label,
          completeList: todos,
          predicate: this.filterTodoPredicate,
        });
      }),
    );
  }

  private filterTodos(todos: NotificationClientTodo[]) {
    this.onlyShowRowsWithContentLabelBS.next(todos);

    if (!this.onlyShowRowsWithContent) {
      return todos;
    }

    return todos.filter(this.filterTodoPredicate);
  }

  // eslint-disable-next-line complexity
  private filterTodoPredicate = ({ todo }: NotificationClientTodo): boolean =>
    todo.AutoRegisteredInvoice > 0 ||
    todo.AutoRegisteredReceipt > 0 ||
    todo.PaymentsToRemediate > 0 ||
    todo.UnhandledDocuments > 0 ||
    todo.UnpaidSupplierInvoices > 0 ||
    todo.EinvoiceReceived > 0 ||
    todo.EinvoiceSent > 0 ||
    todo.TaxAccount !== 'INACTIVE';
}
